import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { PageHeader, btnPrimary, btnTurq, EmptyState } from "../../components/bits";
import { Plus, Upload, Download, FileSpreadsheet } from "lucide-react";
import { StaffCard } from "../../components/staff/StaffCard";
import { StaffFormModal } from "../../components/staff/StaffFormModal";
import { StaffImportModal, StaffReportModal } from "../../components/staff/StaffImportModals";
import { StaffHistoryModal } from "../../components/staff/StaffHistoryModal";

const emptyStaff = { name: "", hours: "", job_title: "", national_hours: "", category: "" };
const STAFF_GROUPS = [
  { key: "inschool", label: "منسوبو المدرسة", match: (s) => s.category !== "خارجيون" },
  { key: "external", label: "متطوعون من خارج المدرسة", match: (s) => s.category === "خارجيون" },
];

export default function Staff() {
  const [staff, setStaff] = useState(null);
  const [opps, setOpps] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyStaff);
  const [logForm, setLogForm] = useState({ opportunity_id: "", hours: 2, date: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [report, setReport] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/portal/staff").then((r) => setStaff(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => {
    load();
    api.get("/portal/opportunities").then((r) => setOpps(r.data)).catch(() => {});
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: (form.name || "").trim(), hours: Number(form.hours) || 0,
        job_title: form.job_title || "", national_hours: Number(form.national_hours) || 0, category: form.category || "" };
      let sid = modal?.member?.id;
      if (modal?.mode === "edit") {
        await api.put(`/portal/staff/${modal.member.id}`, payload);
        toast.success("تم تحديث بيانات الموظف");
      } else {
        const { data: created } = await api.post("/portal/staff", payload);
        sid = created.id || sid;
        toast.success("تمت إضافة الموظف المتطوع");
      }
      if (certFile && sid) {
        const fd = new FormData();
        fd.append("file", certFile);
        await api.post(`/portal/staff/${sid}/certificate`, fd);
        toast.success("تم رفع شهادة الساعات");
      }
      setCertFile(null);
      setModal(null);
      setForm(emptyStaff);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("اختر ملف Excel أولاً");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/portal/staff/import", fd);
      setReport(data);
      setModal(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (s) => {
    setForm({ name: s.name, hours: s.hours || 0, job_title: s.job_title || "", national_hours: s.national_hours || 0, category: s.category || "" });
    setCertFile(null);
    setModal({ mode: "edit", member: s });
  };

  const remove = async (s) => {
    if (!window.confirm(`حذف «${s.name}» وجميع مشاركاته؟`)) return;
    try {
      await api.delete(`/portal/staff/${s.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openHistory = async (s) => {
    setModal({ mode: "history", member: s });
    setLogForm({ opportunity_id: "", hours: 2, date: "" });
    try {
      const { data } = await api.get(`/portal/staff/${s.id}/participations`);
      setHistory(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const logHours = async () => {
    if (!logForm.opportunity_id) {
      toast.error("اختر الفرصة أولاً");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/portal/staff/${modal.member.id}/participate`, logForm);
      toast.success("تم تسجيل المشاركة");
      setLogForm({ opportunity_id: "", hours: 2, date: "" });
      openHistory(modal.member);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const removePart = async (pid) => {
    try {
      await api.delete(`/portal/staff-participations/${pid}`);
      openHistory(modal.member);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!staff) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="staff-page">
      <PageHeader title="الموظفون المتطوعون" sub="سجل منسوبي المدرسة المشاركين في الفرص التطوعية وساعاتهم — يُحتسبون مستقلين عن مؤشر الطلاب">
        <button data-testid="staff-template-btn" onClick={() => window.open(`${API}/portal/staff/template`, "_blank")} className={btnTurq}>
          <FileSpreadsheet className="w-4 h-4" /> قالب Excel
        </button>
        <button data-testid="staff-export-btn" onClick={() => window.open(`${API}/portal/staff/export`, "_blank")} className={btnTurq}>
          <Download className="w-4 h-4" /> تصدير Excel
        </button>
        <button data-testid="staff-import-btn" onClick={() => setModal({ mode: "import" })} className={btnTurq}>
          <Upload className="w-4 h-4" /> استيراد Excel
        </button>
        <button data-testid="staff-add-btn" onClick={() => { setForm(emptyStaff); setCertFile(null); setModal({ mode: "add" }); }} className={btnPrimary}>
          <Plus className="w-4 h-4" /> إضافة موظف متطوع
        </button>
      </PageHeader>

      <div data-testid="staff-cat-chips" className="flex gap-1.5 flex-wrap mb-5">
        {[{ key: "", label: "الكل" }, ...STAFF_GROUPS].map((g) => (
          <button key={g.key || "all"} data-testid={`staff-cat-chip-${g.key || "all"}`} onClick={() => setCatFilter(g.key)}
            className={`rounded-full px-4 py-2 text-[12px] font-bold transition-all ${catFilter === g.key
              ? "bg-turq-600 text-white shadow-md"
              : "bg-surface text-mutext border border-brd hover:scale-105"}`}>
            {g.label} ({staff.filter((s) => !g.key || g.match(s)).length})
          </button>
        ))}
      </div>

      {staff.length === 0 ? (
        <EmptyState testid="staff-empty" text="لا يوجد موظفون متطوعون بعد — أضف أول متطوع من منسوبي المدرسة" />
      ) : (
        <div className="space-y-6">
          {STAFF_GROUPS.filter((g) => !catFilter || g.key === catFilter).map((g) => {
            const items = staff.filter(g.match);
            return (
              <section key={g.key} data-testid={`staff-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-head font-black text-base text-turq-900 dark:text-turq-300">{g.label}</h3>
                  <span data-testid={`staff-group-count-${g.key}`} className="text-[11px] font-bold bg-turq-100 dark:bg-surface2 text-turq-900 dark:text-turq-300 rounded-full px-3 py-1">{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div data-testid={`staff-group-empty-${g.key}`} className="text-center text-xs text-mutext py-6 bg-surface rounded-2xl border border-brd/50">لا يوجد مسجلون في هذه الفئة بعد</div>
                ) : (
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((s) => (
                      <StaffCard key={s.id} s={s} onEdit={openEdit} onDelete={remove} onHistory={openHistory} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <StaffFormModal open={modal?.mode === "add" || modal?.mode === "edit"} mode={modal?.mode} member={modal?.member}
        onClose={() => setModal(null)} form={form} setForm={setForm} certFile={certFile} setCertFile={setCertFile}
        busy={busy} onSave={save} />
      <StaffImportModal open={modal?.mode === "import"} onClose={() => setModal(null)} fileRef={fileRef} busy={busy} onImport={doImport} />
      <StaffReportModal report={report} onClose={() => setReport(null)} />
      <StaffHistoryModal open={modal?.mode === "history"} member={modal?.member} onClose={() => setModal(null)}
        opps={opps} logForm={logForm} setLogForm={setLogForm} history={history} busy={busy}
        onLog={logHours} onRemovePart={removePart} />
    </div>
  );
}
